import { useState, useEffect } from "react";
import { caseService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";
import Button from "@/components/base/Button";
import Card from "@/components/base/Card";

export default function ResumenGeneradorPage() {
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [tipoResumen, setTipoResumen] = useState("ejecutivo");
  const [loading, setLoading] = useState(false);
  const [resumenGenerado, setResumenGenerado] = useState("");

  const [cases, setCases] = useState<any[]>([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadCases() {
      try {
        setError("");
        const data = await caseService.list();
        if (Array.isArray(data)) {
          setCases(data.map(mapDbCaseToFrontend));
        }
      } catch (err: any) {
        console.error("Error loading cases for summary generator:", err);
        setError("Error al cargar casos activos: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoadingCases(false);
      }
    }
    loadCases();
  }, []);

  const selectedCase = cases.find(c => c.id === selectedCaseId);

  const handleGenerate = () => {
    if (!selectedCaseId || !selectedCase) {
      alert("Por favor seleccione un caso activo.");
      return;
    }
    setLoading(true);
    setResumenGenerado("");

    setTimeout(() => {
      setLoading(false);
      const caseObj = selectedCase;
      let text = "";
      if (tipoResumen === "ejecutivo") {
        text = `FICHA DE RESUMEN EJECUTIVO DE CASO IA
Fecha de Emisión: ${new Date().toLocaleDateString("es-CL")}
Folio del Caso: ${caseObj.folio}
Estudiante: ${caseObj.studentName}
Curso: ${caseObj.curso}
Estado Actual: ${caseObj.estado.toUpperCase()}
Prioridad: ${caseObj.prioridad.toUpperCase()}

1. SÍNTESIS DE LA SITUACIÓN:
El caso fue aperturado con fecha ${caseObj.fechaApertura} debido a "${caseObj.titulo}". Según los registros iniciales, se describe lo siguiente: "${caseObj.descripcion}".

2. PROTOCOLOS ACTIVADOS:
De acuerdo al Reglamento de Convivencia y Orientación, el caso se clasificó bajo la categoría de "${caseObj.categoria}" (tipo: ${caseObj.tipo}). Se designó como profesional responsable a ${caseObj.responsableName || "Profesional Asignado"} (${caseObj.responsableRol || "Orientación/Convivencia"}).

3. RECOMENDACIONES Y PLAN DE ACCIÓN:
- Monitorear la asistencia y el comportamiento general en aula de manera diaria.
- Mantener comunicación estrecha con el apoderado responsable del estudiante.
- Coordinar con el equipo de inspectoría general y orientación escolar el cumplimiento de los acuerdos.`;
      } else if (tipoResumen === "derivacion") {
        text = `FICHA SÍNTESIS DE DERIVACIÓN INTERNA IA
Estudiante: ${caseObj.studentName}
Curso: ${caseObj.curso}
Fecha Derivación: ${caseObj.fechaApertura}
Profesional Responsable: ${caseObj.responsableName || "Profesional Asignado"}

MOTIVO DE DERIVACIÓN:
Derivación interna iniciada en el marco de la causa de convivencia/orientación "${caseObj.titulo}". Se requiere intervención y evaluación por especialista del área de apoyo debido a la descripción de la conducta: "${caseObj.descripcion}".

ANTECEDENTES CONDUCTUALES Y SOCIALES:
El estudiante presenta un nivel de riesgo catalogado como ${caseObj.prioridad.toUpperCase()}. Se sugiere realizar:
1. Entrevista clínica individual con el estudiante para evaluar el estado psicosocial.
2. Citación a reunión con el apoderado a fin de levantar anamnesis y recabar antecedentes de apoyo familiar.
3. Sugerencia de derivación externa si se identifica sintomatología que escape del marco de acción escolar.`;
      } else {
        text = `SÍNTESIS PEDAGÓGICA Y ADECUACIONES IA
Estudiante: ${caseObj.studentName}
Curso: ${caseObj.curso}
Profesor Responsable: ${caseObj.responsableName || "Profesional Asignado"}

CONTEXTO EDUCATIVO:
Antecedentes de caso de apoyo escolar en curso por motivo de "${caseObj.titulo}".

ADECUACIONES SUGERIDAS PARA EL AULA:
- Flexibilidad en los tiempos de entrega de tareas y evaluaciones escritas.
- Ubicación preferencial en el aula (cerca del profesor jefe o zona libre de distracciones).
- Refuerzo positivo constante y acompañamiento durante dinámicas grupales para evitar retraimiento.
- Canalizar cualquier desregulación conductual menor por medio del inspector general sin recurrir a medidas extremas inmediatas.`;
      }

      setResumenGenerado(text);
    }, 1500);
  };

  const handleDescargarWord = () => {
    if (!selectedCase) return;
    const element = document.createElement("a");
    const file = new Blob([resumenGenerado], { type: 'application/msword;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = `Resumen_Caso_${selectedCase.studentName.replace(/\s+/g, "_")}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDescargarPDF = () => {
    if (!selectedCase) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Resumen Caso - ${selectedCase.studentName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            h1 { text-align: center; color: #111; font-size: 18px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; }
          </style>
        </head>
        <body>
          <h1>RESUMEN DE CASO GENERADO POR IA</h1>
          <pre>${resumenGenerado}</pre>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(resumenGenerado);
    alert("Copiado al portapapeles con éxito.");
  };

  const activeCases = cases.filter(c => c.estado !== "cerrado");

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up max-w-4xl mx-auto">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Generador de Resúmenes IA</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Genera fichas de síntesis, derivaciones e informes ejecutivos de casos en pocos segundos.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-bold text-foreground-900 mb-3">Parámetros de Generación</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Seleccionar Caso Activo</label>
                {loadingCases ? (
                  <div className="text-xs text-foreground-500 py-2"><i className="ri-loader-4-line animate-spin mr-1" /> Cargando casos...</div>
                ) : (
                  <select
                    value={selectedCaseId}
                    onChange={(e) => setSelectedCaseId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                  >
                    <option value="">Seleccione un caso...</option>
                    {activeCases.map((c) => (
                      <option key={c.id} value={c.id}>{c.studentName} - {c.titulo.substring(0, 30)}...</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tipo de Resumen</label>
                <select
                  value={tipoResumen}
                  onChange={(e) => setTipoResumen(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="ejecutivo">Resumen Ejecutivo de Caso</option>
                  <option value="derivacion">Informe de Derivación Interno</option>
                  <option value="pedagogico">Adecuaciones y Síntesis Pedagógica</option>
                </select>
              </div>

              <Button
                onClick={handleGenerate}
                loading={loading}
                disabled={!selectedCaseId || loading}
                className="w-full bg-accent-500 hover:bg-accent-600 text-white cursor-pointer py-2.5 mt-2"
              >
                {loading ? "Generando..." : "Generar Resumen con IA"}
              </Button>
            </div>
          </Card>
        </div>

        <div className="md:col-span-2">
          {loading ? (
            <Card className="h-64 flex flex-col items-center justify-center text-center">
              <span className="flex items-center justify-center w-8 h-8 mb-3"><i className="ri-loader-4-line text-2xl text-accent-500 animate-spin" /></span>
              <p className="text-sm font-semibold text-foreground-700">Analizando registros y bitácora del caso...</p>
              <p className="text-xs text-foreground-400 mt-1">La IA está estructurando la síntesis del expediente.</p>
            </Card>
          ) : resumenGenerado ? (
            <Card className="space-y-4">
              <div className="flex justify-between items-center border-b border-background-150 pb-2">
                <h3 className="font-bold text-foreground-900 text-sm">Resumen Generado por la IA</h3>
                <div className="flex gap-1.5">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="p-1.5 hover:bg-background-100 rounded-lg text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                    title="Copiar al portapapeles"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                  <button 
                    onClick={handleDescargarWord}
                    className="p-1.5 hover:bg-background-100 rounded-lg text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                    title="Descargar Word"
                  >
                    <i className="ri-file-word-line" />
                  </button>
                  <button 
                    onClick={handleDescargarPDF}
                    className="p-1.5 hover:bg-background-100 rounded-lg text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                    title="Imprimir / Guardar PDF"
                  >
                    <i className="ri-printer-line" />
                  </button>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-4 border border-background-200/60 overflow-y-auto max-h-[400px]">
                <pre className="text-xs text-foreground-850 font-mono whitespace-pre-wrap leading-relaxed">
                  {resumenGenerado}
                </pre>
              </div>
            </Card>
          ) : (
            <Card className="h-64 flex flex-col items-center justify-center text-center border-dashed border-2 border-background-200/80">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-accent-50/50 mb-3 text-accent-600"><i className="ri-sparkling-2-line text-2xl" /></span>
              <p className="text-sm font-semibold text-foreground-850">¿Listo para comenzar?</p>
              <p className="text-xs text-foreground-450 mt-1 max-w-xs">Selecciona un caso del panel izquierdo y presiona el botón para generar el informe automatizado con Inteligencia Artificial.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
